import {
  axiosInstance,
  getConfiguration,
} from '@foxtrotplatform/platform-micro-site-core';
import { AxiosRequestConfig } from 'axios';

import {
  AppValidation,
  CreateApplication,
} from '../../components/Application/types';
import { DeveloperAppConfig } from '../config/developerAppConfig';
import { AppStatusCommand } from '../enums';
import { PatchApplicationDetails } from './models/application';
import { ApiErrorResponse, ApiResponse } from './types';

export const getOrganizationApplications = async (
  organizationId: string,
  teamId: string
): Promise<Application[]> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  config.baseApiUrl;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application`,
    params: {
      organizationId: organizationId,
      teamId: teamId,
    },
    method: 'get',
  };

  const { data }: ApiResponse<Application[]> = await axiosInstance(request);

  return data;
};

export const createApplication = async (
  organizationId: string,
  teamId: string,
  application: CreateApplication
): Promise<Application> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/register`,
    params: {
      organizationId: organizationId,
      teamId: teamId,
    },
    method: 'post',
    data: application,
  };

  const { data }: ApiResponse<Application> = await axiosInstance(request);

  return data;
};

export const relativePathTaken = async (
  relativePath: string
): Promise<{ available: boolean }> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/register/validate`,
    params: {
      path: relativePath,
    },
    method: 'get',
  };
  const { data } = await axiosInstance<ApiResponse<{ available: boolean }>>(
    request
  );
  return data;
};

export const readApplication = async (
  organizationId: string,
  teamId: string,
  applicationId: string
): Promise<Application> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/v2/${applicationId}`,
    params: {
      organizationId: organizationId,
      teamId: teamId,
    },
    method: 'get',
  };

  const { data }: ApiResponse<ApplicationV2Response> = await axiosInstance(
    request
  );

  const appDetails = convertFromApplicationResponse(data);

  return appDetails;
};

export const readApplicationForSubmission = async (
  organizationId: string,
  teamId: string,
  applicationId: string
): Promise<ApplicationSubmission> => {
  const app = await readApplication(organizationId, teamId, applicationId);

  return convertToAppSubmission(app);
};

export const readCategoryOptions = async (): Promise<ApplicationCategory[]> => {
  const categories: ApplicationCategory[] = [
    {
      id: 'dispatch',
      displayName: 'Dispatch',
    },
    {
      id: 'delivery',
      displayName: 'Delivery',
    },
    {
      id: 'resource-management',
      displayName: 'Resource Management',
    },
    {
      id: 'route-planning',
      displayName: 'Route Planning',
    },
    {
      id: 'tracking',
      displayName: 'Tracking',
    },
  ];

  return Promise.resolve(categories);
};

// TODO: Add tests.
export const patchApplicationDetails = async (
  organizationId: string,
  teamId: string,
  appId: string,
  appVersionId: string,
  submission: ApplicationSubmission
): Promise<ApiErrorResponse> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;

  const appPatch: PatchApplicationDetails = {
    listingName: submission.listingName,
    description: submission.description,
    isPrivate: submission.isPrivate,
    isConsoleCompatible: submission.consoleCompatible,
    isMultiTenant: submission.isMultiTenant,
    shortDescription: submission.shortDescription,
    categories: submission.categories,
    appUrls: submission.appUrls,
  };

  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/${appId}/attributes`,
    method: 'patch',
    params: {
      organizationId,
      teamId,
      appVersionId,
    },
    data: appPatch,
  };

  return await axiosInstance<ApiErrorResponse>(request);
};

// TODO: Add tests
export const updateApplicationLifecycle = async (
  organizationId: string,
  teamId: string,
  appId: string,
  appVersionId: string,
  command: AppStatusCommand
): Promise<ApiErrorResponse> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/${appId}/lifecycle`,
    method: 'put',
    params: {
      organizationId,
      teamId,
      appVersionId,
      command,
    },
  };

  return await axiosInstance<ApiErrorResponse>(request);
};

export const publishApplication = async (
  applicationId: string,
  yaml: Blob
): Promise<void> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/${applicationId}`,
    method: 'post',
    data: yaml,
  };

  await axiosInstance(request);
};

export const uploadIcon = async (
  organizationId: string,
  teamId: string,
  appId: string,
  appVersionId: string,
  icon: File
) => {
  const formData = new FormData();
  formData.append('file', icon);
  formData.append('fileName', icon.name);

  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/${appId}/icon`,
    method: 'patch',
    params: {
      organizationId,
      teamId,
      appVersionId,
    },
    data: formData,
    headers: {
      'content-type': 'multipart/form-data',
    },
  };

  return await axiosInstance(request);
};

export const isAppNameAvailable = async (appName: string): Promise<boolean> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/register/validate`,
    params: {
      appListingName: appName,
    },
    method: 'get',
  };
  const { data }: ApiResponse<AppValidation> = await axiosInstance(request);

  return data.appListingName.available;
};

export const appUrlPathExists = async (
  appUrlPath: string
): Promise<boolean> => {
  const config = (await getConfiguration()) as DeveloperAppConfig;
  const request: AxiosRequestConfig = {
    url: `${config.basePortalApiUrl}/application/register/validate`,
    params: {
      appUrlPath: appUrlPath,
    },
    method: 'get',
  };
  const { data }: ApiResponse<AppValidation> = await axiosInstance(request);

  return !data.appUrlPath.available;
};

const convertFromApplicationResponse = (
  response: ApplicationV2Response
): Application => {
  const mostRecentVersion: string = response.versions.reduce<string>(
    (accumulator, current) => {
      return current.version;
    },
    ''
  );

  const version = response.versions.find(
    (v) => v.version === mostRecentVersion
  );

  if (!version) {
    throw new Error("Couldn't find a version.");
  }

  const client = {
    serviceKey: '',
    clientSecret: '',
    clientKey: '',
  };

  // Default credentials for now.
  if (response.clients.length > 0) {
    const defaultClient = response.clients[0];
    client.serviceKey = defaultClient.serviceKey;
    client.clientKey = defaultClient.clientKey || '';
    client.clientSecret = defaultClient.secret;
  }

  const app: Application = {
    appId: response.appId,
    listingId: response.listingId,
    listingName: version.listingName,
    version: version.version,
    versionId: version.appVersionId,
    appUrn: response.urn,
    urlPath: response.urlPath,
    appType: response.appType,
    description: version.shortDescription || '',
    isPrivate: version.isPrivate,
    isConsoleCompatible: version.isConsoleCompatible,
    isMultiTenant: version.isMultiTenant || false,
    honorsCopyright: version.honorsCopyright || false,
    shortDescription: version.shortDescription || '',
    status: version.status,
    landingPageUrl: getLandingPageUrl(version.appUrls),
    appUrls: version.appUrls || [],
    categories: version.categories || [],
    ...client,
  };

  if (version.icons && version.icons.length > 0) {
    app.icon = version.icons[0];
  }

  return app;
};

const convertToAppSubmission = (app: Application): ApplicationSubmission => {
  const submission: ApplicationSubmission = {
    appId: app.appId,
    listingId: app.listingId,
    version: app.version,
    versionId: app.versionId,
    status: app.status,
    listingName: app.listingName,
    appType: app.appType,
    shortDescription: app.shortDescription,
    appUrn: app.appUrn,
    urlPath: app.urlPath,
    description: app.description,
    isPrivate: app.isPrivate,
    consoleCompatible: app.isConsoleCompatible,
    isMultiTenant: false,
    honorsCopyright: false,
    appUrls: app.appUrls || [],
    categories: app.categories || [],
  };

  return submission;
};

const getLandingPageUrl = (urls: { name: string; url: string }[]): string => {
  if (!urls) {
    return '';
  }

  for (const link of urls) {
    if (link.name === 'interface') {
      return link.url;
    }
  }

  return '';
};
